<template>
	<DataTable
		class="p-component p-shadow-2"
		:value="tableData"
		:autoLayout="true"
		:rowHover="true"
		:loading="loading"
		@row-click="onMarkRegion"
		:scrollable="true"
		scrollHeight="flex"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound',
				{ entryName: tableOrigin === 'indicators' ? $t('Squeeze.Training.Indicators')
						: (tableOrigin === 'itemFields' ? $t('Squeeze.Training.Fields')
							: $t('Squeeze.Training.Region') + ' ' + $t('Squeeze.Training.Anchor')) })
			}}
		</template>
		<Column field="id" header="ID" headerClass="header-center" style="width:3rem; text-align: center;" />
		<Column v-if="tableOrigin === 'indicators'" field="tableId" :header="$t('Squeeze.Validation.General.Tables') + ' ID'" style="width:3rem; text-align: center;" />
		<Column v-if="tableOrigin !== 'itemFields'" field="description" :header="$t('Squeeze.Training.Description')" />
		<Column field="locatorId" :header="$t('Squeeze.Training.Locator') + ' ID'" headerClass="header-center" style="width:3rem; text-align: center;" />
		<Column v-if="tableOrigin !== 'itemFields'" field="keyWordPattern" :header="$t('Squeeze.Validation.General.Position') + ' ' + $t('Squeeze.Training.KeyWord')" />
		<Column v-if="tableOrigin === 'itemFields'" field="columnId" :header="$t('Squeeze.Training.Column')">
			<template #body="slotProps">
				{{ getColumnName(slotProps.data.columnId) }}
			</template>
		</Column>
		<Column v-if="tableOrigin === 'itemFields'" field="valuePattern" :header="$t('Squeeze.Training.KeyWord')" />
		<Column v-if="tableOrigin !== 'itemFields'" field="ignoreWhiteSpaces" :header="$t('Squeeze.Training.IgnoreSpaces')">
			<template #body="slotProps">
				<div v-if="JSON.stringify(slotProps.data.ignoreWhiteSpaces) === 'true' || JSON.stringify(slotProps.data.ignoreWhiteSpaces) === 'false'">
					{{ $t("Squeeze.General.Boolean." + JSON.stringify(slotProps.data.ignoreWhiteSpaces)) }}
				</div>
				<div v-else>
					{{ slotProps.data.ignoreWhiteSpaces }}
				</div>
			</template>
		</Column>
		<Column v-if="tableOrigin === 'itemFields'" field="valueIgnoreWhitespace" :header="$t('Squeeze.Training.IgnoreSpaces')">
			<template #body="slotProps">
				<div v-if="JSON.stringify(slotProps.data.valueIgnoreWhitespace) === 'true' || JSON.stringify(slotProps.data.valueIgnoreWhitespace) === 'false'">
					{{ $t("Squeeze.General.Boolean." + JSON.stringify(slotProps.data.valueIgnoreWhitespace)) }}
				</div>
				<div v-else>
					{{ slotProps.data.valueIgnoreWhitespace }}
				</div>
			</template>
		</Column>
		<Column v-if="tableOrigin === 'itemFields'" field="accumulateFoundValues" :header="$t('Squeeze.Training.MultilineReadout')">
			<template #body="slotProps">
				<div v-if="JSON.stringify(slotProps.data.accumulateFoundValues) === 'true' || JSON.stringify(slotProps.data.accumulateFoundValues) === 'false'">
					{{ $t("Squeeze.General.Boolean." + JSON.stringify(slotProps.data.accumulateFoundValues)) }}
				</div>
				<div v-else>
					{{ slotProps.data.accumulateFoundValues }}
				</div>
			</template>
		</Column>
		<Column style="min-width: 9rem; text-align: right;">
			<template #body="slotProps">
				<Button
					v-tooltip="tableOrigin === 'indicators' ? 'Indikator testen' : (tableOrigin === 'itemFields' ? 'Region Feld testen' : 'Region Anker testen')"
					icon="mdi mdi-text-box-search-outline"
					class="p-button p-component p-button-icon-only p-button-lg p-button-rounded p-button-text"
					@click="testData(slotProps.data)"
				/>
				<Button v-tooltip="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button p-component p-button-icon-only p-button-lg p-button-rounded p-button-success p-button-text" @click="changeData(slotProps.data)"/>
				<Button v-tooltip="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button p-button-rounded p-button-danger p-button-text deleteButtonRow" @click="deleteData(slotProps.data)"/>
			</template>
		</Column>
	</DataTable>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {defineComponent, PropType} from "vue";
import {useI18n} from "vue-i18n";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {DocumentTable} from "@dex/squeeze-client-ts";

export default defineComponent({
	name: "FormPositionTrainingTable",
	components: {
		Button,
		DataTable,
		Column,
	},
	props: {
		tableOrigin: {
			type: String,
			default: '',
		},
		tableData: {
			type: Array as PropType<any[]>,
			default: () => [],
		},
		loading: {
			type: Boolean,
			default: false,
		},
		tableId: {
			type: Number,
			default: 0,
		},
		tables: {
			type: Array as PropType<DocumentTable[]>,
			default: () => [],
		},
	},
	emits: [
		'testData',
		'changeData',
		'deleteData',
		'onMarkRegion',
	],
	setup(props, {emit}) {
		const {t} = useI18n();

		/**
		 * Test an indicator
		 * or a region anchor
		 * or an item field
		 * @param rowData
		 */
		const testData = (rowData: any) => {
			emit("testData", rowData);
		}

		/**
		 * Change an indicator
		 * @param rowData
		 */
		const changeData = (rowData: any) => {
			emit("changeData", rowData);
		}

		/**
		 * Deletes an indicator
		 * @param rowData
		 */
		const deleteData = (rowData: any) => {
			emit("deleteData", rowData);
		}

		/**
		 * Click a row to mark
		 * @param event
		 */
		const onMarkRegion = (event: any) => {
			emit("onMarkRegion", event);
		}

		/**
		 * Get column name
		 * @param columnId
		 */
		const getColumnName = (columnId: number) => {
			for (const table of props.tables) {
				if (table.columns) {
					const column = table.columns.find(column => column.id === columnId);
					if (column) return getTranslatedDescription(column);
				}
			}
			return columnId;
		}

		return {
			t,
			testData,
			changeData,
			deleteData,
			onMarkRegion,
			getColumnName,
		}
	},
});
</script>

<style scoped>

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

</style>
